import React, { useContext } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Element } from 'react-scroll';

import {
  Card,
  CardDescription,
  CardDurationInfo,
  CardHeader,
  CardImg,
  CardInfo,
  CardLabel,
  CardLabels,
  CardPlayIcon,
  CardRepeatIcon,
} from '../../../../components/Card';
import ToggleFromList from '../../../../components/ToggleFromList/ToggleFromList';
import { CategoryLabel } from '../../../../components/UI/CategoryLabel';
import DifficultyIcon from '../../../../components/UI/DifficultyIcon';
import { ProgressLine } from '../../../../components/UI/ProgressLine';
import { UserInfoCtx } from '../../../../containers/CommonLayout/CommonLayout';
import { colorsPalette } from '../../../../theme';
import { createCategoryLink } from '../../../../utils/helpers';
import useToggleFromWatchlist from '../../../../utils/hooks/useToggleFromWatchlist';
import Proportional from '../../../CourseProgress/components/Proportional';
import { DEFAULT_URL } from '../../../DiscoverPage/const';
import useStyles from './styles';

export const getCategories = (categories, difficulty?, withDots = true, testName?) => {
  if (!categories) return;

  const categoriesArr: React.ReactNode[] = [];

  categories.forEach((cat) => {
    const categoryLink = createCategoryLink(cat);
    categoriesArr.push(
      <CategoryLabel
        data-testid={testName + 'Category'}
        key={cat?.title}
        url={categoryLink}
        name={cat?.title}
        withDots={withDots}
      />,
    );
  });
  if (difficulty) {
    categoriesArr.push(
      <DifficultyIcon
        data-testid={testName + 'DifficultyIcon'}
        key="difficulty"
        difficulty={difficulty}
      />,
    );
  }

  return categoriesArr;
};

const PathwayVideoCard = ({ video, handleOpenJoinModal }) => {
  const userInfo = useContext(UserInfoCtx);
  const { t } = useTranslation();
  const classes = useStyles(video?.completed)();
  const categories = getCategories(video?.categories, video?.difficulty, false, 'video' + video.id);
  const expertLink = video?.userSlug ? `${DEFAULT_URL}/experts/${video?.userSlug}` : '';

  const [toggleFromWatchlist, toggleFromWatchlistLoading] = useToggleFromWatchlist(
    video.isInWatchlist,
    video.id,
  );

  return (
    <Element id={video?.id}>
      <Card data-testid="videoCard">
        <Proportional ratio={68} className={classes.proportional}>
          {handleOpenJoinModal ? (
            <CardImg
              withLazyLoad
              wrapperClass={classes.imageWrapper}
              imgPath={video?.preview_url}
              onClick={handleOpenJoinModal}
            >
              {video?.progress > 0 && <ProgressLine width={video?.progress} />}
              {video?.completed ? (
                <CardRepeatIcon data-testid={'repeatIcon' + video.id} />
              ) : (
                <CardPlayIcon data-testid={'playIcon' + video.id} />
              )}
              {video?.completed && <CardLabel>{t('common.watched')}</CardLabel>}
            </CardImg>
          ) : (
            <Link
              data-testid={'videoPreviewLink' + video.id}
              className={classes.imageLink}
              to={video?.link}
            >
              <CardImg
                withLazyLoad
                wrapperClass={classes.imageWrapper}
                imgPath={video?.preview_url}
              >
                {video?.progress > 0 && <ProgressLine width={video?.progress} />}
                {video?.completed ? (
                  <CardRepeatIcon data-testid={'repeatIcon' + video.id} />
                ) : (
                  <CardPlayIcon data-testid={'playIcon' + video.id} />
                )}
                {video?.completed && (
                  <CardLabel data-testid={'videoWatched' + video.id}>
                    {t('common.watched')}
                  </CardLabel>
                )}
              </CardImg>
            </Link>
          )}
        </Proportional>
        <CardInfo>
          <CardLabels>
            {video?.new && (
              <CategoryLabel
                bgColor={colorsPalette.primary}
                textColor={colorsPalette.violetAnalyticsCard}
                name={t('common.new')}
                data-testid={'newVideoLabel' + video.id}
              />
            )}
            {categories && categories.length > 0 ? categories : null}
          </CardLabels>
          {video?.title &&
            (handleOpenJoinModal ? (
              <CardHeader
                text={video.title}
                onClick={handleOpenJoinModal}
                className={classes.title}
              />
            ) : (
              <Link
                data-testid={'videoTitle' + video.id}
                className={classes.titleWrapper}
                to={video?.link}
              >
                <CardHeader text={video.title} />
              </Link>
            ))}
          {video?.description && (
            <CardDescription data-testid={'cardDescription' + video.id} text={video?.description} />
          )}
          <div className={classes.infoRow}>
            {(video?.user || video?.duration) && (
              <CardDurationInfo
                durationName={video?.user}
                duration={video?.duration}
                link={expertLink}
              />
            )}
            {userInfo && (
              <ToggleFromList
                disabled={toggleFromWatchlistLoading}
                isAdd={!video.isInWatchlist}
                onClick={toggleFromWatchlist}
              />
            )}
          </div>
        </CardInfo>
      </Card>
    </Element>
  );
};

export default PathwayVideoCard;
